import React, { useRef, useEffect, useState } from 'react';
import { Box, Grid, Card, CardContent, CardActions, Typography, Button, Container } from '@mui/material';
import { styled, keyframes } from '@mui/system';
import image from '../images/Untitled design (17).png';
import chat1 from '../images/expert-chat1.svg';
import abcd from '../images/college-predictors.svg';

const CustomCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  backgroundColor: '#f0f4f8',
  borderRadius: '15px',
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.2)',
  },
}));

const CustomButton = styled(Button)(({ theme }) => ({
  color: '#fff',
  backgroundColor: '#233098',
  fontWeight: 'bold',
  textTransform: 'none',
  marginLeft: theme.spacing(1),
  '&:hover': {
    backgroundColor: '#233098',
  },
}));

const Icon = styled('img')(({ theme }) => ({
  marginRight: theme.spacing(2),
  width: '48px',
  height: '48px',
}));

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const fadeInDown = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const AnimatedImage = styled('img', { shouldForwardProp: (prop) => prop !== 'inView' })(({ inView }) => ({
  maxWidth: '100%',
  animation: inView ? `${slideIn} 1s ease-out 0.3s` : 'none',
  animationFillMode: 'both',
}));

const AnimatedHeading = styled(Typography, { shouldForwardProp: (prop) => prop !== 'inView' })(({ inView }) => ({
  animation: inView ? `${fadeInDown} 1s ease-out 0.3s` : 'none',
  animationFillMode: 'both',
}));

const AnimatedContent = styled(Typography, { shouldForwardProp: (prop) => prop !== 'inView' })(({ inView }) => ({
  animation: inView ? `${fadeInDown} 1s ease-out 0.5s` : 'none',
  animationFillMode: 'both',
}));

const CounsellingSection = () => {
  const [inView, setInView] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    });
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <Container ref={ref}>
      <Grid container sx={{ textAlign: 'center', mb: 4, width: '100%', flexDirection: 'column', justifyContent: 'center' }}>
        <Grid item xs={12} sx={{ mb: 4, width: { xs: '100%', md: '700px' }, mx: 'auto' }}>
          <AnimatedHeading inView={inView} variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600, fontFamily: 'sans-serif',mt:4 }}>
            Counselling
          </AnimatedHeading>
          <AnimatedContent inView={inView} variant="h6" color="textSecondary">
            We ease your biggest doubts with personalized Video Counselling from
            our Curated Experts and Answers from the student community
          </AnimatedContent>
        </Grid>
      </Grid>
      <Grid container spacing={4} alignItems="center" sx={{ flexDirection: { xs: 'column', md: 'row' }, textAlign: { xs: 'center', md: 'left' } }}>
        <Grid item xs={12} md={6} sx={{ mb: { xs: 4, md: 0 } }}>
          <AnimatedImage inView={inView} src={image} alt="Counselling" />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomCard>
            <CardContent sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
              <Icon src={chat1} alt="Expert Counselling Icon" />
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontFamily: 'sans-serif' }}>
                  Expert Counselling
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Personalized Video Counselling from  Experts on Exams, Courses, Colleges and Career selection on our App
                </Typography>
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center',marginLeft:{xs:11,md:0} }}>
              <CustomButton>START NOW</CustomButton>
            </CardActions>
          </CustomCard>
          <CustomCard>
            <CardContent sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
              <Icon src={abcd} alt="College Predictors Icon" />
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontFamily: 'sans-serif' }}>
                  College Predictors
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  Personalized Video Counselling from  Experts on Exams, Courses, Colleges and Career selection on our App
                </Typography>
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center',marginLeft:{xs:11,md:0} }}>
              <CustomButton>START NOW</CustomButton>
            </CardActions>
          </CustomCard>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CounsellingSection;
